/**
 * SoundSettings Component - UI for managing queue sound preferences
 * QUEUE-007: Sound notifications for queue events
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Volume2, VolumeX, Bell, Vibrate } from 'lucide-react';
import { useQueueSounds } from '../../hooks/useQueueSounds';
import { SoundType } from '../../services/QueueSoundManager';

export const SoundSettings: React.FC = () => {
  const { t } = useTranslation();
  const {
    soundConfig,
    setSoundEnabled,
    setSoundVolume,
    setSoundTypeEnabled,
    setVibrationEnabled,
    testSound,
    isInitialized,
  } = useQueueSounds();

  // type → i18n key (note 'turn-alert' maps to 'turnAlert')
  const soundTypes: { type: SoundType; tkey: string }[] = [
    { type: 'notification', tkey: 'notification' },
    { type: 'turn-alert', tkey: 'turnAlert' },
    { type: 'warning', tkey: 'warning' },
    { type: 'success', tkey: 'success' },
  ];

  if (!isInitialized) {
    return (
      <div className="p-4 bg-surface-2 rounded-lg">
        <p className="text-ink-muted">{t('queue.sound.initializing')}</p>
      </div>
    );
  }

  const volumePct = Math.round(soundConfig.volume * 100);

  return (
    <div className="space-y-6 p-4 bg-surface border border-hairline rounded-lg shadow">
      <h3 className="font-sans text-lg font-semibold text-ink flex items-center gap-2">
        <Bell className="w-5 h-5 text-accent" aria-hidden="true" />
        {t('queue.sound.title')}
      </h3>

      {/* Master sound toggle */}
      <div className="flex items-center justify-between p-3 bg-surface-2 border border-hairline rounded-lg">
        <div className="flex items-center gap-3">
          {soundConfig.enabled ? (
            <Volume2 className="w-5 h-5 text-success" aria-hidden="true" />
          ) : (
            <VolumeX className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
          )}
          <div>
            <p className="font-medium text-ink">{t('queue.sound.masterLabel')}</p>
            <p className="text-sm text-ink-muted">{t('queue.sound.masterDesc')}</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={soundConfig.enabled}
            onChange={(e) => setSoundEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-success"></div>
        </label>
      </div>

      {/* Volume control */}
      {soundConfig.enabled && (
        <>
          <div className="space-y-2">
            <label className="flex items-center justify-between text-sm font-medium text-ink-muted">
              <span>{t('queue.sound.volume')}</span>
              <span className="text-ink-subtle tabular-nums">{volumePct}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={volumePct}
              onChange={(e) => setSoundVolume(Number(e.target.value) / 100)}
              className="w-full h-2 bg-surface-3 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, rgb(var(--success)) 0%, rgb(var(--success)) ${volumePct}%, rgb(var(--surface-3)) ${volumePct}%, rgb(var(--surface-3)) 100%)`,
              }}
            />
          </div>

          {/* Vibration toggle */}
          <div className="flex items-center justify-between p-3 bg-surface-2 border border-hairline rounded-lg">
            <div className="flex items-center gap-3">
              <Vibrate className="w-5 h-5 text-info" aria-hidden="true" />
              <div>
                <p className="font-medium text-ink">{t('queue.sound.vibration')}</p>
                <p className="text-sm text-ink-muted">{t('queue.sound.vibrationDesc')}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={soundConfig.vibration || false}
                onChange={(e) => setVibrationEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-info"></div>
            </label>
          </div>

          {/* Individual sound types */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-ink-muted uppercase tracking-wider">
              {t('queue.sound.typesTitle')}
            </h4>
            {soundTypes.map(({ type, tkey }) => {
              const configKey = type.replace('-', '') === 'turnalert' ? 'turnAlert' : type;
              const isEnabled =
                soundConfig.soundTypes[configKey as keyof typeof soundConfig.soundTypes];

              return (
                <div
                  key={type}
                  className="flex items-center justify-between p-3 bg-surface-2 border border-hairline rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink">{t(`queue.sound.types.${tkey}.label`)}</p>
                    <p className="text-sm text-ink-muted">
                      {t(`queue.sound.types.${tkey}.description`)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => testSound(type)}
                      disabled={!isEnabled}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        isEnabled
                          ? 'bg-info/10 text-info hover:bg-info/20'
                          : 'bg-surface-3 text-ink-subtle cursor-not-allowed'
                      }`}
                    >
                      {t('queue.sound.test')}
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => setSoundTypeEnabled(type, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-surface-3 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-success"></div>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
